<template>
  <v-container fluid>
    <template v-if="validarPermiso('dashboard.ver')">
      <v-row align="center">
        <v-col cols="12" lg="4">
          <p class="font-weight-bold mb-0">DASHBOARD DE VENTAS</p>
          <p class="text-caption grey--text mb-0" v-if="dashboard.desde && dashboard.hasta">
            {{ formatFecha(dashboard.desde) }} — {{ formatFecha(dashboard.hasta) }}
          </p>
        </v-col>
        <v-col cols="12" lg="8" class="d-flex flex-wrap align-center justify-lg-end" style="gap: 16px">
          <v-chip-group
            v-model="rangoDias"
            mandatory
            active-class="primary--text"
            @change="seleccionarRangoChip"
          >
            <v-chip v-for="opcion in opcionesRango" :key="opcion" :value="opcion" filter outlined>
              Últimos {{ opcion }} días
            </v-chip>
          </v-chip-group>

          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <v-menu :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details
                  label="Desde"
                  style="max-width: 150px"
                  :value="formatFecha(fechaDesdeCustom)"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="fechaDesdeCustom = ''"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaDesdeCustom" locale="es-es"></v-date-picker>
            </v-menu>
            <v-menu :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details
                  label="Hasta"
                  style="max-width: 150px"
                  :value="formatFecha(fechaHastaCustom)"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="fechaHastaCustom = ''"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaHastaCustom" locale="es-es"></v-date-picker>
            </v-menu>
            <v-btn
              color="info"
              :disabled="!fechaDesdeCustom || !fechaHastaCustom || cargando"
              :loading="cargando"
              @click="aplicarRangoPersonalizado"
            >
              <v-icon left small>mdi-calendar-search</v-icon>
              Analizar periodo
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="cargando">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" :size="60" :width="5" />
        </v-col>
      </v-row>

      <template v-else>
        <div class="d-flex flex-wrap mb-4" style="gap: 16px">
          <v-card outlined style="min-width: 200px">
            <v-card-text>
              <div class="text-caption grey--text text--darken-1 text-no-wrap">VENTAS DEL PERIODO</div>
              <div class="text-h5 font-weight-bold text-no-wrap" v-text="formatMoney(dashboard.totalMonto)"></div>
            </v-card-text>
          </v-card>
          <v-card outlined style="min-width: 200px">
            <v-card-text>
              <div class="text-caption grey--text text--darken-1 text-no-wrap">ÓRDENES DEL PERIODO</div>
              <div class="text-h5 font-weight-bold text-no-wrap" v-text="dashboard.totalOrdenes"></div>
            </v-card-text>
          </v-card>
          <v-card outlined style="border-left: 4px solid #2a78d6; min-width: 220px">
            <v-card-text>
              <div class="text-caption grey--text text--darken-1 text-no-wrap">
                PRIMERA COMPRA ({{ porcentajeOrdenes(dashboard.primeraCompra.ordenes) }}% de órdenes)
              </div>
              <div class="text-h6 font-weight-bold text-no-wrap" v-text="formatMoney(dashboard.primeraCompra.monto)"></div>
              <div class="text-caption text-no-wrap">{{ dashboard.primeraCompra.ordenes }} órdenes</div>
            </v-card-text>
          </v-card>
          <v-card outlined style="border-left: 4px solid #eb6834; min-width: 220px">
            <v-card-text>
              <div class="text-caption grey--text text--darken-1 text-no-wrap">
                RECOMPRA ({{ porcentajeOrdenes(dashboard.recompra.ordenes) }}% de órdenes)
              </div>
              <div class="text-h6 font-weight-bold text-no-wrap" v-text="formatMoney(dashboard.recompra.monto)"></div>
              <div class="text-caption text-no-wrap">{{ dashboard.recompra.ordenes }} órdenes</div>
            </v-card-text>
          </v-card>
        </div>

        <v-row>
          <v-col cols="12" lg="8">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Ventas por día</v-card-title>
              <v-card-text>
                <div style="position: relative; height: 400px">
                  <line-chart :chart-data="dataGraficoVentas" :options="optionsGraficoVentas"></line-chart>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" lg="4">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Primera compra vs. Recompra</v-card-title>
              <v-card-text>
                <div style="position: relative; height: 400px">
                  <horizontal-bar-chart
                    :chart-data="dataGraficoComparativo"
                    :options="optionsGraficoComparativo"
                  ></horizontal-bar-chart>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Ventas por departamento</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersDepartamento"
                  :items="dashboard.porDepartamento"
                  item-key="departamento"
                  disable-sort
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  height="320px"
                  class="text-no-wrap"
                >
                  <template v-slot:item.monto="{ item }">{{ formatMoney(item.monto) }}</template>
                  <template v-slot:item.porcentaje="{ item }">{{ item.porcentaje }}%</template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Ventas por cupón</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersCupon"
                  :items="dashboard.porCupon"
                  item-key="cupon"
                  disable-sort
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  height="320px"
                  class="text-no-wrap"
                >
                  <template v-slot:item.monto="{ item }">{{ formatMoney(item.monto) }}</template>
                  <template v-slot:item.porcentaje="{ item }">{{ item.porcentaje }}%</template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Ventas por tipo de pedido</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersTipo"
                  :items="dashboard.porTipoMedida"
                  item-key="tipo"
                  disable-sort
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  height="320px"
                  class="text-no-wrap"
                >
                  <template v-slot:item.monto="{ item }">{{ formatMoney(item.monto) }}</template>
                  <template v-slot:item.porcentaje="{ item }">{{ item.porcentaje }}%</template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="text-subtitle-1">Ventas por tipo de luna</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="headersTipo"
                  :items="dashboard.porTipoLuna"
                  item-key="tipo"
                  disable-sort
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  height="320px"
                  class="text-no-wrap"
                >
                  <template v-slot:item.monto="{ item }">{{ formatMoney(item.monto) }}</template>
                  <template v-slot:item.porcentaje="{ item }">{{ item.porcentaje }}%</template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>
  </v-container>
</template>

<script>
import LineChart from '../Componentes/Graficos/Line';
import HorizontalBarChart from '../Componentes/Graficos/HorizontalBar';

const COLOR_VENTAS = '#2a78d6';
const COLOR_PRIMERA_COMPRA = '#2a78d6';
const COLOR_RECOMPRA = '#eb6834';

export default {
  components: {
    LineChart,
    HorizontalBarChart,
  },
  data() {
    return {
      opcionesRango: [7, 15, 30],
      rangoDias: 7,
      fechaDesdeCustom: '',
      fechaHastaCustom: '',
      usandoRangoPersonalizado: false,
      cargando: false,
      dashboard: {
        desde: '',
        hasta: '',
        grafico: { labels: [], valores: [] },
        totalMonto: 0,
        totalOrdenes: 0,
        primeraCompra: { monto: 0, ordenes: 0 },
        recompra: { monto: 0, ordenes: 0 },
        porDepartamento: [],
        porCupon: [],
        porTipoMedida: [],
        porTipoLuna: [],
      },
      headersDepartamento: [
        { text: 'DEPARTAMENTO', value: 'departamento' },
        { text: 'MONTO', value: 'monto' },
        { text: 'ÓRDENES', value: 'ordenes' },
        { text: '% DEL TOTAL', value: 'porcentaje' },
      ],
      headersCupon: [
        { text: 'CUPÓN', value: 'cupon' },
        { text: 'MONTO', value: 'monto' },
        { text: 'ÓRDENES', value: 'ordenes' },
        { text: '% DEL TOTAL', value: 'porcentaje' },
      ],
      headersTipo: [
        { text: 'TIPO', value: 'tipo' },
        { text: 'MONTO', value: 'monto' },
        { text: 'CANTIDAD', value: 'cantidad' },
        { text: '% DEL TOTAL', value: 'porcentaje' },
      ],
      dataGraficoVentas: {},
      dataGraficoComparativo: {},
      optionsGraficoVentas: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        layout: {
          padding: { bottom: 8 },
        },
        scales: {
          xAxes: [
            {
              display: true,
              gridLines: { drawOnChartArea: false },
              ticks: {
                display: true,
                autoSkip: true,
                maxTicksLimit: 10,
                maxRotation: 0,
                minRotation: 0,
                padding: 6,
              },
            },
          ],
          yAxes: [{ ticks: { beginAtZero: true } }],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => this.formatMoney(tooltipItem.yLabel),
          },
        },
      },
      optionsGraficoComparativo: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ ticks: { beginAtZero: true } }],
        },
      },
    };
  },

  methods: {
    formatMoney(valor) {
      const numero = Number(valor) || 0;
      return (numero < 0 ? '-S/ ' : 'S/ ') + Math.abs(numero).toFixed(2);
    },

    formatFecha(fecha) {
      return fecha ? this.$moment(fecha).format('DD/MM/YYYY') : '';
    },

    porcentajeOrdenes(ordenes) {
      if (!this.dashboard.totalOrdenes) return 0;
      return Math.round((ordenes / this.dashboard.totalOrdenes) * 100);
    },

    seleccionarRangoChip() {
      this.usandoRangoPersonalizado = false;
      this.fechaDesdeCustom = '';
      this.fechaHastaCustom = '';
      this.cargarDashboard();
    },

    aplicarRangoPersonalizado() {
      if (!this.fechaDesdeCustom || !this.fechaHastaCustom) return;
      this.usandoRangoPersonalizado = true;
      this.cargarDashboard();
    },

    cargarDashboard() {
      this.cargando = true;
      const params = this.usandoRangoPersonalizado
        ? { desde: this.fechaDesdeCustom, hasta: this.fechaHastaCustom }
        : { dias: this.rangoDias };

      this.axios({
        method: 'GET',
        url: 'api/panel/ventas/dashboard',
        params,
      })
        .then((response) => {
          this.dashboard = response.data;
          this.generarGraficos();
        })
        .catch((error) => {
          console.log(error);
          this.$toasted.error('Ocurrio un error al cargar el dashboard de ventas', {
            icon: 'mdi-close',
          });
        })
        .finally(() => {
          this.cargando = false;
        });
    },

    generarGraficos() {
      const cantidadPuntos = this.dashboard.grafico.labels.length;

      this.dataGraficoVentas = {
        labels: this.dashboard.grafico.labels,
        datasets: [
          {
            label: 'Ventas',
            borderColor: COLOR_VENTAS,
            backgroundColor: 'rgba(42, 120, 214, 0.15)',
            pointBackgroundColor: COLOR_VENTAS,
            fill: true,
            lineTension: 0.2,
            borderWidth: 2,
            pointRadius: cantidadPuntos > 20 ? 0 : 3,
            pointHoverRadius: 5,
            data: this.dashboard.grafico.valores,
          },
        ],
      };

      this.dataGraficoComparativo = {
        labels: [
          ['Primera compra', this.etiquetaComparativo(this.dashboard.primeraCompra)],
          ['Recompra', this.etiquetaComparativo(this.dashboard.recompra)],
        ],
        datasets: [
          {
            label: 'Monto',
            backgroundColor: [COLOR_PRIMERA_COMPRA, COLOR_RECOMPRA],
            data: [this.dashboard.primeraCompra.monto, this.dashboard.recompra.monto],
          },
        ],
      };
    },

    etiquetaComparativo(bloque) {
      return this.formatMoney(bloque.monto) + ' · ' + bloque.ordenes + ' órdenes';
    },
  },

  created() {
    // Se carga solo al entrar a esta pestaña (Dashboard), nunca automaticamente
    // al iniciar sesion ni desde otras vistas.
    if (this.validarPermiso('dashboard.ver')) {
      this.cargarDashboard();
    }
  },
};
</script>

<style scoped>
.small {
  max-height: 300px;
}
</style>
